/**
 * The free-trial organization (MAR-1452, D19): a licence always belongs to a user AND an organization,
 * so a free-tier user is put in an ordinary organization of their own, created lazily on their first
 * free use (`ensureFreeTrialOrganization`, `server/services/organizations/membership.ts`).
 *
 * It is named after the language the app is rendering in when it is created — "Prueba Gratis" in a
 * Spanish UI, "Free Trial org" in an English one — and it is in El Salvador, the one signup country.
 * Onboarding recognises both names (`isFreeTrialOrgName`) to offer a rename when the user leaves the
 * trial for a paid plan; nothing else about the organization is special.
 */

/** The plan of a free-trial member's seat (billing's `free_tier`; `server/utils/planAccess.ts` names it too). */
export const FREE_TIER_PLAN_ID = 'free_tier'

/** The default names, by UI locale. Spanish is the app's default locale (`DEFAULT_UI_LOCALE`). */
export const FREE_TRIAL_ORG_NAMES = {
  es: 'Prueba Gratis',
  en: 'Free Trial org',
} as const

/** The country a free-trial organization is created in: El Salvador, the only signup country today. */
export const FREE_TRIAL_ORG_COUNTRY = 'SV'

/**
 * The name for a free-trial organization created while the app renders in `uiLanguage` — either the
 * locale code (`en`, `en-US`) or the English language name `event.context.uiLanguage` carries
 * (`English`, `server/utils/languagePreference.ts`). English gets the English name; every other
 * language gets the Spanish default, as the app itself does.
 */
export function freeTrialOrgName(uiLanguage: string | null | undefined): string {
  const value = String(uiLanguage ?? '').trim().toLowerCase()
  return value === 'english' || value === 'en' || value.startsWith('en-')
    ? FREE_TRIAL_ORG_NAMES.en
    : FREE_TRIAL_ORG_NAMES.es
}

/** Whether `name` is one of the default free-trial names (surrounding whitespace and case ignored). */
export function isFreeTrialOrgName(name: string | null | undefined): boolean {
  const value = String(name ?? '').trim().toLowerCase()
  return Object.values(FREE_TRIAL_ORG_NAMES).some(n => n.toLowerCase() === value)
}

/**
 * Whether a LOADED profile is a free-tier user, whom the client routes to the free chat (`/chat`) rather than
 * the app (MAR-1452, D19): a user with no organization yet (whose first free use makes their trial organization), or a
 * free-trial member — `free_trial` from `/api/profile`, decided server-side from the seat plan and the
 * subscription, never from the organization's name. `false` with no profile at all; a caller that must not
 * route on a PARTIAL profile (the login redirects) checks that it carries `organization_name` first.
 *
 * Browsing only: opening `/app` or `/app/setup` on purpose still lands both on the plan step, which is how a
 * free user buys (`gateFor`, `shared/onboarding/flow.ts`).
 */
export function isFreeTierProfile(profile: { organization_name?: string | null, free_trial?: boolean } | null | undefined): boolean {
  if (!profile) return false
  return !profile.organization_name?.trim() || profile.free_trial === true
}
